import { CurrentUserState } from '../../../../mainv2/states/CurrentUserState';
import type { RouterStore } from '../../../../common/mobx-react-router';
import { UserFormState } from './UserFormState';
import { RemoteResourceV3, updateRemoteResourceV3 } from '../../../../commonv3/api/RemoteResourceV3';
import { MembershipPlanApis } from '../../../apis/membershipPlans';
import { MembershipItemIndexView, MembershipPlanIndexView, MembershipPlanKey } from '../../../types/membershipPlans';
import { EnhancedLocationDescriptor, V3Routes } from '../../../routes';
import { action, computed, observable, makeObservable } from 'mobx';
import { MembershipItemId, MembershipPlanId } from '../../../../commonv3/types/membershipPlans';
import { CreditCardState } from '../../../components/molecules/payment/states/CreditCardFormState';
import { EntranceFeeApis } from '../../../apis/entranceFees';
import { EntranceFeeShowView } from '../../../types/entranceFees';
import { SubmitState } from '../../../../commonv3/states/SubmitState';
import { MembershipApplicationApis } from '../../../apis/membershipApplications';
import { SheRavenClient } from '../../../../commonv2/modules/ravenClient';
import moment from 'moment-timezone';
import { CouponState } from './CouponState';
import { BaseListState } from '../../../../commonv3/states/LocationListState';
import { LocationId } from '../../../../commonv3/types/locations';
import { ServiceAgreementKey, ServiceAgreementState } from './../../../states/ServiceAgreementState';
import { V3MembershipPlanKey } from '../../../../mainv2/types/user';
import { UserAddressFormState } from './UserAddressFormState';

export class ApplicationPageState {
  userState: CurrentUserState = null;
  routerState: RouterStore = null;
  userFormState: UserFormState = new UserFormState();
  addressFormState: UserAddressFormState = new UserAddressFormState();
  cardState: CreditCardState = null;
  baseListState: BaseListState = new BaseListState();
  serviceAgreementState: ServiceAgreementState = new ServiceAgreementState();
  selectedLocationId = -1;
  startedFrom: EnhancedLocationDescriptor = null;
  submitState = new SubmitState();
  initState = new SubmitState();
  startDate: moment.Moment | null = null;
  couponState = new CouponState();
  plans: RemoteResourceV3<MembershipPlanIndexView[]> = RemoteResourceV3.loading();
  entranceFeeResource: RemoteResourceV3<EntranceFeeShowView> = RemoteResourceV3.loading();
  selectedPlanId = null;
  selectedItemId = null;
  entranceInstallments = 1;
  membershipItemOrderId?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any = null;

  constructor(userState: CurrentUserState, routerState: RouterStore) {
    makeObservable(this, {
      selectedLocationId: observable,
      startedFrom: observable,
      submitState: observable,
      initState: observable,
      startDate: observable,
      couponState: observable,
      plans: observable,
      entranceFeeResource: observable,
      selectedPlanId: observable,
      selectedItemId: observable,
      entranceInstallments: observable,
      error: observable,
      initing: computed,
      canGoBack: computed,
      userEmail: computed,
      selectPlan: action.bound,
      selectedItem: computed,
      selectedPlan: computed,
      selectItem: action.bound,
      selectHomeLocation: action.bound,
      validateHomeLocationAndNext: action.bound,
      setEntranceInstallments: action.bound,
      entranceFee: computed,
      setStartDate: action.bound,
      validatePlanAndNext: action.bound,
      validateUserAndNext: action.bound,
      validateStartDateAndNext: action.bound,
      validateCardAndNext: action.bound,
      validateUserAddressAndNext: action.bound,
      validateCouponCode: action.bound,
      clearCoupons: action.bound,
      submitting: computed,
      submit: action.bound,
      complete: action.bound,
      completeWithError: action.bound,
      restart: action.bound,
      membershipItemOrderId: observable,
    });

    this.userState = userState;
    this.routerState = routerState;
    this.cardState = new CreditCardState(userState);
  }

  async init() {
    await this.initState.operate(async () => {
      await this.couponState.init();
      return await Promise.all([
        this.userState.reloadUser(),
        this.reloadPlans(),
        this.reloadEntranceFee(),
        this.cardState.init(),
        this.baseListState.init(),
        this.serviceAgreementState.init(ServiceAgreementKey.STANDARD),
      ]);
    });

    if (this.userState.hasLikesActiveSubscription()) {
      this.routerState.replace('/home');
    } else {
      this.addressFormState.init();
      if (!this.userState.isSignedIn()) {
        this.userFormState.init();
        if (V3Routes.applicationUserInfo().pathname === this.routerState.location.pathname) {
          this.startedFrom = V3Routes.applicationUserInfo();
        } else {
          this.startedFrom = V3Routes.applicationSignIn();
        }
      } else {
        const plan_keys = [V3MembershipPlanKey.MAKUAKE_STANDARD, V3MembershipPlanKey.MAKUAKE_TRIAL];
        const membership_plan_keys = this.userState.currentUser.membership_plan_keys;

        if (plan_keys.some((plan_key) => membership_plan_keys.includes(plan_key))) {
          if (this.userState.homeLocation.isHomeLocationSet) {
            this.startedFrom = V3Routes.applicationPlanSelect();
          } else {
            this.startedFrom = V3Routes.applicationHomeLocationSelect();
          }
        } else {
          this.startedFrom = V3Routes.applicationStartDateSelect();
        }
      }
      this.routerState.replace(this.startedFrom);
    }
  }
  get initing(): boolean {
    return this.initState.submitting;
  }

  get canGoBack(): boolean {
    if (!this.startedFrom) {
      return false;
    }
    return this.startedFrom.pathname !== this.routerState.location.pathname;
  }

  get userEmail() {
    if (this.userState.isSignedIn()) {
      return this.userState.currentUser.email;
    }
    return this.userFormState.fields.email.getDirtyValue();
  }

  async reloadEntranceFee() {
    await updateRemoteResourceV3(
      this.entranceFeeResource,
      (v) => (this.entranceFeeResource = v),
      () => EntranceFeeApis.show(false, this.userEmail, this.couponState.appliedCouponCode)
    );
  }

  async reloadPlans() {
    const plans = await updateRemoteResourceV3(
      this.plans,
      (v) => (this.plans = v),
      () => MembershipPlanApis.findAll(false, this.startDate, this.couponState.appliedCouponCode, this.userEmail)
    );
    const regularPlan = plans.find((p) => p.key === MembershipPlanKey.REGULAR);
    if (regularPlan) {
      this.selectPlan(regularPlan.id);
    }
  }

  selectPlan(planId: MembershipPlanId) {
    this.selectedPlanId = planId;
    if (this.selectedPlan && this.selectedPlan.items.length === 1) {
      this.selectedItemId = this.selectedPlan.items[0].id;
    }
  }

  get selectedItem(): MembershipItemIndexView {
    if (!this.selectedPlan) {
      return null;
    }
    return this.selectedPlan.items.find((i) => i.id === this.selectedItemId);
  }

  get selectedPlan(): MembershipPlanIndexView {
    return this.plans.getOrElse([]).find((p) => p.id === this.selectedPlanId);
  }

  selectItem(itemId: MembershipItemId) {
    this.selectedItemId = itemId;
  }

  selectHomeLocation(locationId: LocationId | null) {
    this.selectedLocationId = locationId;
  }

  async validateHomeLocationAndNext() {
    if (this.selectedLocationId != null && this.selectedLocationId <= 0) {
      throw new Error('Home location must be selected');
    }
    this.routerState.push(V3Routes.applicationPlanSelect());
  }

  async setEntranceInstallments(installments: number) {
    this.entranceInstallments = installments;
  }

  get entranceFee() {
    return this.entranceFeeResource.get();
  }

  setStartDate(date) {
    this.startDate = moment(date);
  }

  async validatePlanAndNext() {
    this.routerState.push(V3Routes.applicationPaymentMethod());
  }

  async validateUserAndNext() {
    await this.submitState.operate(async () => {
      await this.userFormState.validate();
      if (this.userFormState.hasErrors()) {
        return;
      } else {
        this.routerState.push(V3Routes.applicationStartDateSelect());
      }
    });
  }

  async validateStartDateAndNext() {
    if (!this.startDate) {
      return;
    }
    this.reloadPlans();
    this.routerState.push(V3Routes.applicationHomeLocationSelect());
  }

  async validateCardAndNext() {
    await this.submitState.operate(async () => {
      if (this.cardState.editMode) {
        if (!(await this.cardState.validateNewCard())) {
          return;
        }
      }
      this.error = null;
      this.routerState.push(V3Routes.applicationAddressInfo());
    });
  }

  async validateUserAddressAndNext() {
    await this.addressFormState.validate();
    if (this.addressFormState.hasErrors()) {
      return;
    } else {
      this.routerState.push(V3Routes.applicationConfirmation());
    }
  }

  async validateCouponCode() {
    await this.submitState.operate(async () => {
      await this.couponState.submit(this.userEmail, this.selectedPlanId);
      if (this.couponState.appliedCouponCode) {
        await Promise.all([this.reloadPlans(), this.reloadEntranceFee()]);
      }
    });
  }

  async clearCoupons() {
    await this.submitState.operate(async () => {
      this.couponState.clearCoupons();
      await Promise.all([this.reloadPlans(), this.reloadEntranceFee()]);
    });
  }

  get submitting(): boolean {
    return this.submitState.submitting;
  }

  async submit() {
    try {
      await this.submitState.operate(async () => {
        this.error = null;
        const response = await MembershipApplicationApis.create({
          user: this.userState.isSignedIn() ? undefined : this.userFormState.getDirtyValue(),
          user_address: this.addressFormState.getDirtyValue(),
          card: this.cardState.newToken ? { token: this.cardState.newToken } : undefined,
          plan_item_id: this.selectedItemId,
          entrance_installments: this.entranceInstallments,
          effective_date: this.startDate ? this.startDate.format('YYYY-MM-DD') : undefined,
          email: this.userEmail,
          coupon_code: this.couponState.appliedCouponCode,
          home_location_id: this.selectedLocationId,
        });
        await this.userState.reloadUser();
        // Handle partial failures of orders
        /*
         * response.orders には 入会金[0]の order と 最初の会員権[1]の order が含まれている
         * 暫定対応でインデックスで指定。改めてフィルター処理を用いて対応が必要
         */
        this.membershipItemOrderId = response.orders[1]?.id;
        if (response.orders.some((order) => order.payments.some((o) => o.status === 'error'))) {
          this.error = response.orders;
        }
      });
      this.routerState.push(V3Routes.applicationComplete());
    } catch (e) {
      SheRavenClient.captureException(e, {
        user: this.userState.currentUser,
        selectedPlan: this.selectedPlan,
        selectedItem: this.selectedItem,
      });
      this.error = e;
    }
  }

  complete() {
    // A temporary solution to force refresh the user info
    // API client doesn't track the cookie update
    location.href = V3Routes.home().stringify();
  }

  completeWithError() {
    location.href = V3Routes.payments().stringify();
  }

  restart() {
    location.href = V3Routes.application().stringify();
  }
}
